/*
  <product-status> (chunk 04): keeps the product page in step with the selected variant's
  fulfillment status: the status strip, the add to cart button label, the hidden Ships line
  item property, the back-in-stock button, the SKU in the eyebrow and the cable builder links.

  sections/main-product.liquid prints a JSON map of variant id → status inside this element
  (snippets/product-status.liquid with format 'json', plus sku and available). On a variant
  change Ride's product-info.js swaps price, media and the variant id, resets the button label
  to "Add to cart" or "Sold out" and then publishes PUB_SUB_EVENTS.variantChange; this element
  re-applies the status from the map at that point. An option combination that doesn't exist
  publishes nothing, so the empty variant id input is watched too.

  Sold out: Klaviyo's back-in-stock button replaces the disabled button only once Klaviyo's
  onsite.js has really loaded (layout/theme.liquid marks <html> with data-klaviyo-onsite="loaded"
  from the script's onload) or Klaviyo has injected its own trigger. Until then an outline link to
  the contact page takes that place, so the buy box never shows a button that does nothing.
*/
if (!customElements.get('product-status')) {
  customElements.define(
    'product-status',
    class ProductStatus extends HTMLElement {
      // How long after the window's load event to keep watching for Klaviyo.
      static KLAVIYO_WATCH_MS = 10000;

      constructor() {
        super();
        this.onVariantChange = this.onVariantChange.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onClick = this.onClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onKlaviyoChange = this.onKlaviyoChange.bind(this);
        this.stopWatchingKlaviyo = this.stopWatchingKlaviyo.bind(this);
        this.updateCables = this.updateCables.bind(this);
        this.unsubscribers = [];
      }

      connectedCallback() {
        this.root = this.closest('product-info') || this.parentElement;
        try {
          this.map = JSON.parse(this.querySelector('[data-product-status-map]').textContent);
        } catch (error) {
          this.map = { variants: {} };
        }
        if (!this.map.variants) this.map.variants = {};

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribers.push(subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange));
          // product-info.js resets the quantity to the new variant's minimum without an input event.
          this.unsubscribers.push(subscribe(PUB_SUB_EVENTS.quantityUpdate, () => this.updateCables()));
        }
        this.root.addEventListener('change', this.onChange);
        this.root.addEventListener('input', this.onChange);
        this.root.addEventListener('click', this.onClick);
        this.root.addEventListener('keydown', this.onKeydown);

        this.render(this.currentVariantId());
        // Klaviyo's script may finish after this one; check the back-in-stock button again then.
        if (document.readyState !== 'complete') {
          window.addEventListener('load', () => this.render(this.currentVariantId()), { once: true });
        }
        this.watchKlaviyo();
      }

      disconnectedCallback() {
        this.unsubscribers.forEach((unsubscribe) => unsubscribe());
        this.unsubscribers = [];
        this.root?.removeEventListener('change', this.onChange);
        this.root?.removeEventListener('input', this.onChange);
        this.root?.removeEventListener('click', this.onClick);
        this.root?.removeEventListener('keydown', this.onKeydown);
        this.stopWatchingKlaviyo();
      }

      // Klaviyo can become ready after the first render: onsite.js fires klaviyo:onsite-loaded from
      // its onload, and Klaviyo injects its trigger into the buy box some time after that. Render
      // again when either happens, for up to KLAVIYO_WATCH_MS after the window's load event.
      watchKlaviyo() {
        if (!this.root.querySelector('[data-notify]') || this.klaviyoReady()) return;

        document.addEventListener('klaviyo:onsite-loaded', this.onKlaviyoChange);
        const container = this.root.querySelector('.product-buy');
        if (container && typeof MutationObserver === 'function') {
          this.klaviyoObserver = new MutationObserver(this.onKlaviyoChange);
          this.klaviyoObserver.observe(container, { childList: true, subtree: true });
        }

        const stopLater = () => {
          this.klaviyoTimer = setTimeout(this.stopWatchingKlaviyo, ProductStatus.KLAVIYO_WATCH_MS);
        };
        if (document.readyState === 'complete') {
          stopLater();
        } else {
          this.onWindowLoad = stopLater;
          window.addEventListener('load', stopLater, { once: true });
        }
      }

      onKlaviyoChange() {
        if (!this.klaviyoReady()) return;
        this.stopWatchingKlaviyo();
        this.render(this.currentVariantId());
      }

      stopWatchingKlaviyo() {
        document.removeEventListener('klaviyo:onsite-loaded', this.onKlaviyoChange);
        this.klaviyoObserver?.disconnect();
        this.klaviyoObserver = null;
        clearTimeout(this.klaviyoTimer);
        if (this.onWindowLoad) window.removeEventListener('load', this.onWindowLoad);
        this.onWindowLoad = null;
      }

      get form() {
        return this.root.querySelector('product-form form');
      }

      get idInput() {
        return this.form?.querySelector('input[name="id"]') || null;
      }

      currentVariantId() {
        if (this.idInput) return this.idInput.value;

        // No buy buttons block: fall back to the variant picker's selected variant.
        const selected = this.root.querySelector('variant-selects [data-selected-variant]');
        if (selected) {
          try {
            const variant = JSON.parse(selected.textContent);
            if (variant?.id) return String(variant.id);
          } catch (error) {
            // Ignore and use the only variant below.
          }
        }
        const ids = Object.keys(this.map.variants);
        return ids.length === 1 ? ids[0] : '';
      }

      onVariantChange({ data } = {}) {
        const variant = data?.variant;
        if (!variant) return;

        const id = String(variant.id);
        if (!this.map.variants[id]) return;

        // A quick add modal on the same page publishes with the same section id; only follow
        // events for the variant this element's own form now holds.
        const current = this.currentVariantId();
        if (current && current !== id) return;

        this.render(id);
      }

      onChange(event) {
        const target = event.target;
        if (!(target instanceof HTMLInputElement)) return;

        if (target.name === 'quantity') {
          this.updateCables();
        } else if (event.type === 'change' && target === this.idInput && target.value === '') {
          // Unavailable option combination: product-info.js shows "Unavailable" and publishes nothing.
          this.render('');
        }
      }

      onClick(event) {
        const notify = event.target.closest('[data-notify]');
        if (!notify || !this.root.contains(notify)) return;

        // Klaviyo handled the click on this element itself (it prevents the "#" navigation).
        if (event.defaultPrevented) return;
        event.preventDefault();

        // Klaviyo's legacy script inserts its own trigger next to the add to cart button; the
        // stylesheet hides it so only this button shows, and the click is handed to it here.
        const injected = this.injectedTrigger();
        if (injected) {
          event.stopPropagation();
          injected.click();
          return;
        }

        if (!this.klaviyoReady()) {
          // Nothing can open Klaviyo's form any more: swap in the contact link and move focus to it.
          this.render(this.currentVariantId());
          const contact = this.root.querySelector('[data-notify-contact]');
          if (contact && !contact.hidden) contact.focus();
        }
        // Otherwise the click carries on to Klaviyo's delegated .klaviyo-bis-trigger handler.
      }

      // The back-in-stock link has role="button", so Space activates it like Enter does.
      onKeydown(event) {
        if (event.key !== ' ' || !event.target.matches?.('[data-notify]')) return;
        event.preventDefault();
        event.target.click();
      }

      // True only on a real load signal: onsite.js's onload in layout/theme.liquid, or a trigger
      // Klaviyo injected. window.klaviyo proves nothing: Klaviyo's app embed defines it as a queue
      // stub (a Proxy) before any Klaviyo script has loaded, and it stays one when the scripts
      // are blocked.
      klaviyoReady() {
        return document.documentElement.dataset.klaviyoOnsite === 'loaded' || !!this.injectedTrigger();
      }

      injectedTrigger() {
        const container = this.root.querySelector('.product-buy') || this.root;
        return (
          Array.from(container.querySelectorAll('.klaviyo-bis-trigger')).find(
            (element) => !element.hasAttribute('data-notify')
          ) || null
        );
      }

      render(id) {
        const entry = id ? this.map.variants[id] : null;
        const fields = entry?.fields || null;

        this.renderStrip(fields);
        this.renderButtons(id, fields);
        this.renderShips(fields);
        this.renderSku(entry);
        this.updateCables(id, entry);
      }

      renderStrip(fields) {
        const strip = this.root.querySelector('[data-status-strip]');
        if (!strip) return;

        if (!fields) {
          strip.hidden = true;
          return;
        }

        const status = fields.status;
        strip.dataset.status = status;
        strip.classList.remove('ok', 'backorder', 'preorder', 'soldout');
        strip.classList.add(status === 'in_stock' ? 'ok' : status);
        const title = strip.querySelector('[data-status-title]');
        if (title) title.textContent = fields.label;
        // A product that doesn't ship (data-no-shipping, e.g. gift cards) prints its email delivery
        // line as the in-stock sentence, so switching variants never brings back shipping copy.
        strip.hidden = status === 'in_stock' && strip.dataset.showInStock === 'false';
      }

      renderButtons(id, fields) {
        const form = this.form;
        const submit = form?.querySelector('[name="add"]');
        if (!submit) return;

        const label = submit.querySelector('span');
        // Disabled means product-info.js has already written "Sold out" or "Unavailable".
        if (fields && label && !submit.disabled) label.textContent = fields.button;

        // Sold out: Klaviyo's back-in-stock button once Klaviyo is ready, else the contact link,
        // in place of the disabled button (same size, so nothing below moves).
        const soldOut = !!(fields && fields.status === 'soldout');
        const notify = form.querySelector('[data-notify]');
        const contact = form.querySelector('[data-notify-contact]');
        const showNotify = !!(notify && soldOut && this.klaviyoReady());
        const showContact = !!(contact && soldOut && !showNotify);
        if (notify) {
          notify.hidden = !showNotify;
          if (id) notify.dataset.variantId = id;
        }
        if (contact) contact.hidden = !showContact;
        submit.hidden = showNotify || showContact;
        // The strip's sold-out sentence asks for an email, so it shows only with this button.
        const strip = this.root.querySelector('[data-status-strip]');
        if (strip) strip.dataset.backInStock = showNotify ? 'on' : 'off';
        // Klaviyo's replace_anchor option hides the add to cart button inline; undo that once
        // the variant can be bought again.
        if (!showNotify && submit.style.display === 'none') submit.style.removeProperty('display');
      }

      renderShips(fields) {
        const input = this.form?.querySelector('[data-ships-property]');
        if (!input) return;

        const value = (fields && fields.line_property) || '';
        input.value = value;
        input.disabled = value === '';
      }

      renderSku(entry) {
        const wrapper = this.root.querySelector('[data-eyebrow-sku]');
        if (!wrapper) return;

        const sku = (entry && entry.sku) || '';
        const value = wrapper.querySelector('[data-eyebrow-sku-value]');
        if (value) value.textContent = sku;
        wrapper.hidden = sku === '';
      }

      quantity() {
        const input = this.root.querySelector('input[name="quantity"]');
        const quantity = parseInt(input?.value, 10);
        return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
      }

      updateCables(id = this.currentVariantId(), entry = id ? this.map.variants[id] : null) {
        const links = this.root.querySelectorAll('[data-cable-link]');
        if (!links.length) return;

        const quantity = this.quantity();
        // Same rule as snippets/product-cables.liquid: no store parameter for a variant that can't be bought.
        const store = entry && entry.available ? `${id}:${quantity}` : '';
        links.forEach((link) => {
          link.href = ProductStatus.withStoreParam(link.dataset.baseHref || link.href, store);
        });

        const foot = this.root.querySelector('[data-cables-foot]');
        if (foot) foot.hidden = store === '';
        const quantityText = this.root.querySelector('[data-cables-qty]');
        if (quantityText) quantityText.textContent = String(quantity);
      }

      // Appends store=<variant id>:<quantity> to a cable builder share URL, before any #fragment,
      // without re-encoding the parameters already there.
      static withStoreParam(url, store) {
        if (!store) return url;
        const hashIndex = url.indexOf('#');
        const main = hashIndex >= 0 ? url.slice(0, hashIndex) : url;
        const hash = hashIndex >= 0 ? url.slice(hashIndex) : '';
        return `${main}${main.includes('?') ? '&' : '?'}store=${store}${hash}`;
      }
    }
  );
}
